import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Stack,
  Fab,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Divider
} from '@mui/material';
import {
  Edit,
  Delete,
  Search,
  Add,
  PersonAdd,
  AdminPanelSettings,
  Person,
  Refresh,
  Sell as PriceListIcon
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { User, UserCreateRequest, UserUpdateRequest, UserRole, PriceList, UserPriceListAssignmentInput } from '../../types';
import { useAuth } from '../../context/AuthContext';

const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin':
      return 'error';
    case 'user':
      return 'primary';
    default:
      return 'default';
  }
};

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'admin':
      return <AdminPanelSettings fontSize="small" />;
    case 'user':
      return <Person fontSize="small" />;
    default:
      return <Person fontSize="small" />;
  }
};

export const UserManagement: React.FC = () => {
  const { user: currentUser, refreshUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Listas de precios asignables + su tope de descuento (CLAUDE.md §4/§8.11)
  // - la edición real se movió acá desde Listas de Precios el 2026-09-04.
  // assignedListIds/discountPctByListId son estado compartido entre el
  // diálogo de creación y el de edición (se resetean al abrir cada uno).
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [assignedListIds, setAssignedListIds] = useState<number[]>([]);
  const [discountPctByListId, setDiscountPctByListId] = useState<Record<number, string>>({});

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form states
  const [formData, setFormData] = useState<UserCreateRequest>({
    email: '',
    name: '',
    password: '',
    role: 'user',
    telefono: '',
    erp_vendedor_number: '',
    mo_max_discount_pct: null,
    flete_max_discount_pct: null
  });

  const [editFormData, setEditFormData] = useState<UserUpdateRequest>({
    email: '',
    name: '',
    role: 'user',
    is_active: true,
    erp_vendedor_number: '',
    mo_max_discount_pct: null,
    flete_max_discount_pct: null
  });

  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadUsers();
    loadPriceLists();
  }, []);

  useEffect(() => {
    // Clear messages after 5 seconds
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters: any = {};
      if (searchTerm) filters.email = searchTerm;
      if (roleFilter) filters.role = roleFilter;
      if (statusFilter !== '') filters.is_active = statusFilter === 'true';

      const usersData = await apiService.users.getAll(filters);
      setUsers(usersData);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError('Error al cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadPriceLists = async () => {
    try {
      const data = await apiService.priceLists.getAll(true);
      setPriceLists(data);
    } catch (err) {
      console.error('Error loading price lists:', err);
    }
  };

  // Arma el payload {assignments: [{price_list_id, max_discount_pct}]} a
  // partir del estado de checkboxes/tope actual (CLAUDE.md §4/§8.11) -
  // compartido por el create y el edit handler de abajo.
  const buildPriceListAssignments = (): UserPriceListAssignmentInput[] =>
    assignedListIds.map((price_list_id) => {
      const raw = discountPctByListId[price_list_id];
      const max_discount_pct = raw !== undefined && raw !== '' ? parseFloat(raw) : null;
      return { price_list_id, max_discount_pct };
    });

  const handleSearch = () => {
    loadUsers();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setTimeout(loadUsers, 100);
  };

  const handleCreateUser = async () => {
    try {
      setError(null);
      const newUser = await apiService.users.create(formData);
      // Solo llamar si se tildó alguna lista - un usuario recién creado no
      // tiene asignaciones previas que limpiar (CLAUDE.md §4/§8.11).
      if (assignedListIds.length > 0) {
        await apiService.users.assignPriceLists(newUser.id, buildPriceListAssignments());
      }
      setSuccess('Usuario creado exitosamente');
      setCreateDialog(false);
      setFormData({ email: '', name: '', password: '', role: 'user', telefono: '', mo_max_discount_pct: null, flete_max_discount_pct: null });
      setAssignedListIds([]);
      setDiscountPctByListId({});
      loadUsers();
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.response?.data?.detail || 'Error al crear el usuario');
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;

    try {
      setError(null);
      await apiService.users.update(selectedUser.id, editFormData);
      // Siempre se reenvía el conjunto completo (aunque haya quedado
      // vacío) - es el mismo patrón que ya usaba el diálogo de asignación
      // en Listas de Precios: el admin ve y confirma el estado completo,
      // no un delta.
      await apiService.users.assignPriceLists(selectedUser.id, buildPriceListAssignments());

      // Bug: un admin editando su PROPIO usuario no veía el cambio
      // reflejado hasta volver a iniciar sesión, porque AuthContext solo
      // hidrataba `user` una vez al login/mount (ver AuthContext.tsx).
      if (currentUser?.id === selectedUser.id) {
        await refreshUser();
      }

      setSuccess('Usuario actualizado exitosamente');
      setEditDialog(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.detail || 'Error al actualizar el usuario');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setError(null);
      await apiService.users.delete(selectedUser.id);
      setSuccess('Usuario eliminado exitosamente');
      setDeleteDialog(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.detail || 'Error al eliminar el usuario');
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) return;

    try {
      setError(null);
      await apiService.users.changePassword(selectedUser.id, newPassword);
      setSuccess('Contraseña cambiada exitosamente');
      setChangePasswordDialog(false);
      setNewPassword('');
      loadUsers();
    } catch (err: any) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    }
  };

  const openCreateDialog = () => {
    setFormData({ email: '', name: '', password: '', role: 'user', telefono: '', erp_vendedor_number: '' });
    setAssignedListIds([]);
    setDiscountPctByListId({});
    setCreateDialog(true);
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      email: user.email,
      name: user.name,
      role: user.role as UserRole,
      is_active: user.is_active,
      telefono: user.telefono || '',
      erp_vendedor_number: user.erp_vendedor_number || '',
      mo_max_discount_pct: user.mo_max_discount_pct ?? null,
      flete_max_discount_pct: user.flete_max_discount_pct ?? null
    });
    setAssignedListIds(user.price_list_assignments.map((a) => a.price_list_id));
    const initialPct: Record<number, string> = {};
    user.price_list_assignments.forEach((a) => {
      if (a.max_discount_pct !== undefined && a.max_discount_pct !== null) {
        initialPct[a.price_list_id] = String(a.max_discount_pct);
      }
    });
    setDiscountPctByListId(initialPct);
    setEditDialog(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteDialog(true);
  };

  // Picker de listas de precios + tope de descuento (CLAUDE.md §4/§8.11) -
  // compartido entre el diálogo de creación y el de edición. Mismo patrón
  // (checkbox + campo de tope al lado) que tenía el diálogo "Asignar
  // usuarios" que existía en PriceListManagement.tsx antes de esta
  // relocación.
  const renderPriceListPicker = () => (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Listas de Precios
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Solo puede elegir estas listas al armar un presupuesto. El tope de descuento limita
        cuánto puede descontar en un ambiente presupuestado con esa lista (CLAUDE.md §8.11) -
        vacío = sin tope configurado.
      </Typography>
      {priceLists.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No hay listas de precios activas en este país.
        </Typography>
      ) : (
        <List dense sx={{ maxHeight: 220, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {priceLists.map((pl) => {
            const checked = assignedListIds.includes(pl.id);
            return (
              <ListItemButton
                key={pl.id}
                onClick={() =>
                  setAssignedListIds((prev) =>
                    checked ? prev.filter((id) => id !== pl.id) : [...prev, pl.id]
                  )
                }
                sx={{ alignItems: 'center' }}
              >
                <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                <ListItemText primary={pl.name} />
                {checked && (
                  <TextField
                    label="Tope desc. (%)"
                    type="number"
                    size="small"
                    value={discountPctByListId[pl.id] ?? ''}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      setDiscountPctByListId((prev) => ({ ...prev, [pl.id]: e.target.value }))
                    }
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    sx={{ width: 140, ml: 1 }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = !roleFilter || user.role === roleFilter;
    const matchesStatus = statusFilter === '' || user.is_active.toString() === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Check if current user is admin
  if (currentUser?.role !== 'admin') {
    return (
      <Box p={3}>
        <Alert severity="error">
          No tienes permisos para acceder a esta sección. Solo los administradores pueden gestionar usuarios.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestión de Usuarios
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={openCreateDialog}
          size="large"
        >
          Crear Usuario
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros de Búsqueda
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Buscar por email o nombre"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 250 }}
            />
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Rol</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as string)}
                label="Rol"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="admin">Administrador</MenuItem>
                <MenuItem value="user">Usuario</MenuItem>
                <MenuItem value="ml_operator">Mercado Libre</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as string)}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Activo</MenuItem>
                <MenuItem value="false">Inactivo</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={handleSearch}
              startIcon={<Search />}
            >
              Buscar
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<Refresh />}
            >
              Limpiar
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Usuarios ({filteredUsers.length})
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Listas de Precios</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha Creación</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {user.id === currentUser?.id && (
                            <Chip
                              label="Tú"
                              size="small"
                              color="secondary"
                              sx={{ mr: 1 }}
                            />
                          )}
                          {user.email}
                        </Box>
                      </TableCell>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.telefono || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getRoleIcon(user.role)}
                          label={user.role === 'admin' ? 'Administrador' : user.role === 'ml_operator' ? 'Mercado Libre' : 'Usuario'}
                          color={getRoleColor(user.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.price_list_assignments.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            Sin listas asignadas
                          </Typography>
                        ) : (
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {user.price_list_assignments.map((a) => (
                              <Tooltip
                                key={a.price_list_id}
                                title={
                                  a.max_discount_pct != null
                                    ? `Tope de descuento: ${a.max_discount_pct}%`
                                    : 'Sin tope de descuento configurado'
                                }
                              >
                                <Chip
                                  icon={<PriceListIcon fontSize="small" />}
                                  label={
                                    a.max_discount_pct != null
                                      ? `${a.price_list_name} (${a.max_discount_pct}%)`
                                      : a.price_list_name
                                  }
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Activo' : 'Inactivo'}
                          color={user.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString('es-ES')}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar usuario">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(user)}
                            color="success"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        {user.id !== currentUser?.id && (
                          <Tooltip title="Eliminar usuario">
                            <IconButton
                              size="small"
                              onClick={() => openDeleteDialog(user)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No se encontraron usuarios
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Crear Nuevo Usuario</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
              <TextField
                fullWidth
                label="Nombre"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
              <TextField
                fullWidth
                label="Contraseña"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                helperText="Opcional: dejar en blanco para una cuenta que solo inicia sesión con Google"
              />
              <TextField
                fullWidth
                label="Teléfono"
                value={formData.telefono || ''}
                onChange={(e) => setFormData({...formData, telefono: e.target.value})}
              />
              <TextField
                fullWidth
                label="Número de Vendedor (ERP)"
                value={formData.erp_vendedor_number || ''}
                onChange={(e) => setFormData({...formData, erp_vendedor_number: e.target.value})}
                helperText="Número que Bejerman necesita para el campo Vendedor en cada NP/NPH (CLAUDE.md §8.11). Dejar en blanco si este usuario no envía al ERP."
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Desc. aprobado Mano de Obra (%)"
                  value={formData.mo_max_discount_pct ?? ''}
                  onChange={(e) => setFormData({...formData, mo_max_discount_pct: e.target.value === '' ? null : Number(e.target.value)})}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  helperText="Tope de descuento pre-aprobado para Mano de Obra (§8.7). Vacío = sin tope."
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Desc. aprobado Flete (%)"
                  value={formData.flete_max_discount_pct ?? ''}
                  onChange={(e) => setFormData({...formData, flete_max_discount_pct: e.target.value === '' ? null : Number(e.target.value)})}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  helperText="Tope de descuento pre-aprobado para Flete (§8.7). Vacío = sin tope."
                />
              </Stack>
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value as UserRole})}
                  label="Rol"
                >
                  <MenuItem value="user">Usuario</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                  <MenuItem value="ml_operator">Mercado Libre</MenuItem>
                </Select>
              </FormControl>
              <Divider />
              {renderPriceListPicker()}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={!formData.email || !formData.name}
          >
            Crear Usuario
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editar Usuario</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                required
              />
              <TextField
                fullWidth
                label="Nombre"
                value={editFormData.name}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                required
              />
              <TextField
                fullWidth
                label="Teléfono"
                value={editFormData.telefono || ''}
                onChange={(e) => setEditFormData({...editFormData, telefono: e.target.value})}
              />
              <TextField
                fullWidth
                label="Número de Vendedor (ERP)"
                value={editFormData.erp_vendedor_number || ''}
                onChange={(e) => setEditFormData({...editFormData, erp_vendedor_number: e.target.value})}
                helperText="Número que Bejerman necesita para el campo Vendedor en cada NP/NPH (CLAUDE.md §8.11). Dejar en blanco si este usuario no envía al ERP."
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Desc. aprobado Mano de Obra (%)"
                  value={editFormData.mo_max_discount_pct ?? ''}
                  onChange={(e) => setEditFormData({...editFormData, mo_max_discount_pct: e.target.value === '' ? null : Number(e.target.value)})}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  helperText="Tope de descuento pre-aprobado para Mano de Obra (§8.7). Vacío = sin tope."
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Desc. aprobado Flete (%)"
                  value={editFormData.flete_max_discount_pct ?? ''}
                  onChange={(e) => setEditFormData({...editFormData, flete_max_discount_pct: e.target.value === '' ? null : Number(e.target.value)})}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  helperText="Tope de descuento pre-aprobado para Flete (§8.7). Vacío = sin tope."
                />
              </Stack>
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({...editFormData, role: e.target.value as UserRole})}
                  label="Rol"
                  disabled={selectedUser?.id === currentUser?.id} // No permitir cambiar el propio rol
                >
                  <MenuItem value="user">Usuario</MenuItem>
                  <MenuItem value="admin">Administrador</MenuItem>
                  <MenuItem value="ml_operator">Mercado Libre</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={editFormData.is_active}
                    onChange={(e) => setEditFormData({...editFormData, is_active: e.target.checked})}
                    disabled={selectedUser?.id === currentUser?.id} // No permitir desactivarse a sí mismo
                  />
                }
                label="Usuario Activo"
              />
              <Divider />
              {renderPriceListPicker()}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>
            Cancelar
          </Button>
          <Button 
            variant="outlined" 
            color="warning"
            onClick={() => setChangePasswordDialog(true)}
            sx={{ mr: 'auto' }}
          >
            Cambiar Contraseña
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEditUser}
            disabled={!editFormData.email || !editFormData.name}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Eliminar Usuario</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que quieres eliminar al usuario <strong>{selectedUser?.name}</strong> ({selectedUser?.email})?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteUser}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialog} onClose={() => setChangePasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cambiar Contraseña - {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ingresa una nueva contraseña para {selectedUser?.email}
            </Typography>
            <TextField
              fullWidth
              label="Nueva Contraseña"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setChangePasswordDialog(false);
            setNewPassword('');
          }}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleChangePassword}
            disabled={!newPassword || newPassword.length < 6}
          >
            Cambiar Contraseña
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
